import React, { useState, useEffect, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import { useConsents } from "../hooks/useConsents";
import "../styles/AnalyticsDashboard.css";

// Phase 15.2 — lazy-loaded so the leaflet chunk stays out of the main bundle.
const GeoMapPanel = React.lazy(() => import("./GeoMapPanel"));

interface StatusBreakdown {
  applied: number;
  interview_scheduled: number;
  rejected: number;
  offered: number;
  accepted: number;
}

interface WeeklyData {
  week: string;
  startDate: string;
  count: number;
}

interface TopCompany {
  company: string;
  count: number;
}

interface PlatformBenchmarks {
  platformResponseRate: number;
  platformInterviewToOfferRate: number;
  platformAvgAppsPerWeek: number;
  platformAvgTimeToResponseDays: number;
  sampleSize: number;
}

interface TopLocation {
  location: string;
  count: number;
}

interface Overview {
  totalApplications: number;
  savedJobs: number;
  statusBreakdown: StatusBreakdown;
  responseRate: number;
  successRate: number;
  weeklyApplications: WeeklyData[];
  topCompanies: TopCompany[];
  topLocations: TopLocation[];
  averageTimeToResponse: string;
  interviewToOfferRate: number;
  benchmarks: PlatformBenchmarks;
}

interface FunnelStage {
  stage: string;
  count: number;
  percentage: number;
}

interface FunnelData {
  funnel: FunnelStage[];
  rejected: { count: number; percentage: number };
  insights: string[];
}

interface SkillDemand {
  skill: string;
  demand: number;
  growth: string;
  userHas?: boolean;
}

interface Trends {
  skillDemand: SkillDemand[];
  salaryTrends: { junior: any; mid: any; senior: any };
  hotJobs: { title: string; openings: number; growth: string }[];
  industryGrowth: { industry: string; growth: number }[];
  recommendations: string[];
  currencySymbol?: string;
}

const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { consents, loading: consentsLoading } = useConsents();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [trends, setTrends] = useState<Trends | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "funnel" | "trends" | "benchmarks">("overview");

  // Helper function to create pie chart path
  const createPieSlice = (
    startAngle: number,
    endAngle: number,
    radius: number,
    label?: string
  ) => {
    const startRad = (Math.PI / 180) * startAngle;
    const endRad = (Math.PI / 180) * endAngle;
    const x1 = 100 + radius * Math.cos(startRad);
    const y1 = 100 + radius * Math.sin(startRad);
    const x2 = 100 + radius * Math.cos(endRad);
    const y2 = 100 + radius * Math.sin(endRad);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M 100 100 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  // Helper function to create donut chart path
  const createDonutSlice = (
    startAngle: number,
    endAngle: number,
    outerRadius: number,
    innerRadius: number
  ) => {
    const startRad = (Math.PI / 180) * startAngle;
    const endRad = (Math.PI / 180) * endAngle;
    
    const x1Outer = 100 + outerRadius * Math.cos(startRad);
    const y1Outer = 100 + outerRadius * Math.sin(startRad);
    const x2Outer = 100 + outerRadius * Math.cos(endRad);
    const y2Outer = 100 + outerRadius * Math.sin(endRad);
    
    const x1Inner = 100 + innerRadius * Math.cos(endRad);
    const y1Inner = 100 + innerRadius * Math.sin(endRad);
    const x2Inner = 100 + innerRadius * Math.cos(startRad);
    const y2Inner = 100 + innerRadius * Math.sin(startRad);
    
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    
    return (
      `M ${x1Outer} ${y1Outer} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2Outer} ${y2Outer} ` +
      `L ${x1Inner} ${y1Inner} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x2Inner} ${y2Inner} Z`
    );
  };

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      const [overviewRes, funnelRes, trendsRes] = await Promise.all([
        axiosInstance.get("/auth/analytics/overview"),
        axiosInstance.get("/auth/analytics/application-funnel"),
        axiosInstance.get("/auth/analytics/trends"),
      ]);
      setOverview(overviewRes.data);
      setFunnel(funnelRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const renderOverviewTab = () => {
    if (!overview) return null;

    return (
      <div className="ad-tab-content">
        {/* Key Metrics */}
        <div className="ad-metrics-grid">
          <div className="ad-metric-card">
            <span className="ad-metric-value">{overview.totalApplications}</span>
            <span className="ad-metric-label">Total Applications</span>
          </div>
          <div className="ad-metric-card">
            <span className="ad-metric-value">{overview.savedJobs}</span>
            <span className="ad-metric-label">Saved Jobs</span>
          </div>
          <div className="ad-metric-card highlight">
            <span className="ad-metric-value">{overview.responseRate}%</span>
            <span className="ad-metric-label">Response Rate</span>
          </div>
          <div className="ad-metric-card success">
            <span className="ad-metric-value">{overview.successRate}%</span>
            <span className="ad-metric-label">Success Rate</span>
          </div>
        </div>

        {/* Status Breakdown - Pie Chart */}
        <div className="ad-section">
          <h3>📊 Application Status Distribution</h3>
          <div className="ad-pie-donut-container">
            {/* Pie Chart */}
            <div className="ad-chart-wrapper">
              <svg viewBox="0 0 200 200" className="ad-pie-chart">
                {(() => {
                  let currentAngle = -90;
                  const colors: Record<string, string> = {
                    applied: "#6366f1",
                    interview_scheduled: "#f59e0b",
                    offered: "#10b981",
                    rejected: "#ef4444",
                    accepted: "#22c55e",
                  };

                  return Object.entries(overview.statusBreakdown).map(([status, count]) => {
                    const percentage = overview.totalApplications > 0 ? (count / overview.totalApplications) * 100 : 0;
                    const sliceAngle = (percentage / 100) * 360;
                    const path = createPieSlice(currentAngle, currentAngle + sliceAngle, 70);
                    currentAngle += sliceAngle;

                    return (
                      <path
                        key={status}
                        d={path}
                        fill={colors[status] || "#9ca3af"}
                        stroke="#ffffff"
                        strokeWidth="2"
                        style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                        opacity="0.9"
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.9')}
                      />
                    );
                  });
                })()}
              </svg>
            </div>

            {/* Legend */}
            <div className="ad-chart-legend">
              {Object.entries(overview.statusBreakdown).map(([status, count]) => {
                const percentage = overview.totalApplications > 0 ? (count / overview.totalApplications) * 100 : 0;
                const colors: Record<string, string> = {
                  applied: "#6366f1",
                  interview_scheduled: "#f59e0b",
                  offered: "#10b981",
                  rejected: "#ef4444",
                  accepted: "#22c55e",
                };

                return (
                  <div key={status} className="ad-legend-item">
                    <div
                      className="ad-legend-color"
                      style={{ backgroundColor: colors[status] || "#9ca3af" }}
                    />
                    <span className="ad-legend-label">
                      {status.replace(/_/g, " ")} · {count} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Weekly Applications Chart */}
        <div className="ad-section">
          <h3>📈 Applications Over Time</h3>
          <div className="ad-chart">
            <div className="ad-bar-chart">
              {overview.weeklyApplications.map((week, idx) => (
                <div key={idx} className="ad-bar-item">
                  <div 
                    className="ad-bar"
                    style={{ 
                      height: `${Math.max(week.count * 20, 10)}px`,
                      backgroundColor: week.count > 0 ? "#6366f1" : "#e5e7eb"
                    }}
                  >
                    {week.count > 0 && <span className="ad-bar-value">{week.count}</span>}
                  </div>
                  <span className="ad-bar-label">{week.week}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Companies - Donut Chart */}
        <div className="ad-section">
          <h3>🏢 Top Applied Companies</h3>
          {overview.topCompanies.length > 0 ? (
            <div className="ad-pie-donut-container">
              {/* Donut Chart */}
              <div className="ad-chart-wrapper">
                <svg viewBox="0 0 200 200" className="ad-donut-chart">
                  {(() => {
                    let currentAngle = -90;
                    const colors = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#22c55e", "#ec4899", "#14b8a6", "#8b5cf6"];
                    const totalApplications = overview.topCompanies.reduce((sum, c) => sum + c.count, 0);

                    return overview.topCompanies.slice(0, 8).map((company, idx) => {
                      const percentage = (company.count / totalApplications) * 100;
                      const sliceAngle = (percentage / 100) * 360;
                      const path = createDonutSlice(currentAngle, currentAngle + sliceAngle, 70, 45);
                      currentAngle += sliceAngle;

                      return (
                        <path
                          key={idx}
                          d={path}
                          fill={colors[idx % colors.length]}
                          stroke="#ffffff"
                          strokeWidth="2"
                          style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                          opacity="0.9"
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.9')}
                        />
                      );
                    });
                  })()}
                </svg>
              </div>

              {/* Legend */}
              <div className="ad-chart-legend ad-compact-legend">
                {overview.topCompanies.slice(0, 8).map((company, idx) => {
                  const totalApplications = overview.topCompanies.reduce((sum, c) => sum + c.count, 0);
                  const percentage = (company.count / totalApplications) * 100;
                  const colors = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#22c55e", "#ec4899", "#14b8a6", "#8b5cf6"];

                  return (
                    <div key={idx} className="ad-legend-item">
                      <div
                        className="ad-legend-color"
                        style={{ backgroundColor: colors[idx % colors.length] }}
                      />
                      <span className="ad-legend-label">
                        {company.company} · {company.count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="ad-empty">No applications yet</p>
          )}
        </div>

        {/* Response Time */}
        <div className="ad-section">
          <h3>⏱️ Average Response Time</h3>
          <div className="ad-response-time">
            <span className="ad-response-value">{overview.averageTimeToResponse}</span>
            <p>Average time to hear back from employers</p>
          </div>
        </div>

        {/* Applications by Location */}
        <div className="ad-section">
          <h3>🗺️ Applications by Location</h3>
          {(overview.topLocations || []).length === 0 ? (
            <p className="ad-empty-text">No location data yet. Start applying to jobs to see your breakdown.</p>
          ) : (
            <div className="ad-location-list">
              {(overview.topLocations || []).map((loc, idx) => {
                const maxCount = overview.topLocations[0]?.count || 1;
                const barWidth = Math.round((loc.count / maxCount) * 100);
                const colors = ["#6366f1", "#f59e0b", "#10b981", "#ec4899", "#0ea5e9", "#8b5cf6", "#ef4444", "#14b8a6"];
                const color = colors[idx % colors.length];
                return (
                  <div key={loc.location} className="ad-location-row">
                    <div className="ad-location-label">📍 {loc.location}</div>
                    <div className="ad-location-bar-wrap">
                      <div className="ad-location-bar" style={{ width: `${barWidth}%`, background: color }} />
                    </div>
                    <div className="ad-location-count" style={{ color }}>{loc.count} app{loc.count !== 1 ? "s" : ""}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Phase 15.2 — Geo Map (design 5.4) */}
        <Suspense
          fallback={
            <div className="ad-section">
              <h3>🗺️ Locations Explored</h3>
              <p style={{ color: "#6b7280" }}>Loading map…</p>
            </div>
          }
        >
          <GeoMapPanel />
        </Suspense>
      </div>
    );
  };

  const renderFunnelTab = () => {
    if (!funnel) return null;

    const maxCount = Math.max(...funnel.funnel.map(f => f.count));

    return (
      <div className="ad-tab-content">
        <div className="ad-section">
          <h3>🔄 Application Funnel</h3>
          <div className="ad-funnel">
            {funnel.funnel.map((stage, idx) => (
              <div key={idx} className="ad-funnel-stage">
                <div 
                  className="ad-funnel-bar"
                  style={{ 
                    width: `${maxCount > 0 ? (stage.count / maxCount) * 100 : 100}%`,
                    backgroundColor: idx === 0 ? "#6366f1" : 
                      idx === 1 ? "#f59e0b" : 
                      idx === 2 ? "#10b981" : "#22c55e"
                  }}
                >
                  <span className="ad-funnel-label">{stage.stage}</span>
                  <span className="ad-funnel-stats">
                    {stage.count} ({stage.percentage}%)
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Rejected */}
          <div className="ad-rejected-section">
            <p>
              <span className="ad-rejected-icon">❌</span>
              Rejected: {funnel.rejected.count} ({funnel.rejected.percentage}%)
            </p>
          </div>
        </div>

        {/* Insights */}
        <div className="ad-section">
          <h3>💡 Insights & Recommendations</h3>
          <div className="ad-insights">
            {funnel.insights.map((insight, idx) => (
              <div key={idx} className="ad-insight-item">
                <span className="ad-insight-icon">💡</span>
                <span>{insight}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Conversion Rates */}
        <div className="ad-section">
          <h3>📊 Conversion Rates</h3>
          <div className="ad-conversion-grid">
            <div className="ad-conversion-card">
              <span className="ad-conversion-value">
                {funnel.funnel[0].count > 0 
                  ? Math.round((funnel.funnel[1].count / funnel.funnel[0].count) * 100) 
                  : 0}%
              </span>
              <span className="ad-conversion-label">Applied → Interview</span>
            </div>
            <div className="ad-conversion-card">
              <span className="ad-conversion-value">
                {funnel.funnel[1].count > 0 
                  ? Math.round((funnel.funnel[2].count / funnel.funnel[1].count) * 100) 
                  : 0}%
              </span>
              <span className="ad-conversion-label">Interview → Offer</span>
            </div>
            <div className="ad-conversion-card">
              <span className="ad-conversion-value">
                {funnel.funnel[2].count > 0 
                  ? Math.round((funnel.funnel[3].count / funnel.funnel[2].count) * 100) 
                  : 0}%
              </span>
              <span className="ad-conversion-label">Offer → Accept</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTrendsTab = () => {
    if (!trends) return null;

    return (
      <div className="ad-tab-content">
        {/* Skill Demand */}
        <div className="ad-section">
          <h3>🎯 Skills in Demand</h3>
          <div className="ad-skills-demand">
            {trends.skillDemand.map((skill, idx) => (
              <div key={idx} className={`ad-skill-item ${skill.userHas ? "has-skill" : ""}`}>
                <div className="ad-skill-header">
                  <span className="ad-skill-name">
                    {skill.userHas && <span className="ad-skill-check">✓</span>}
                    {skill.skill}
                  </span>
                  <span className="ad-skill-growth">{skill.growth}</span>
                </div>
                <div className="ad-skill-bar">
                  <div 
                    className="ad-skill-fill"
                    style={{ width: `${skill.demand}%` }}
                  />
                </div>
                <span className="ad-skill-demand">{skill.demand}% demand</span>
              </div>
            ))}
          </div>
        </div>

        {/* Salary Trends */}
        <div className="ad-section">
          <h3>💰 Salary Trends</h3>
          <div className="ad-salary-grid">
            {Object.entries(trends.salaryTrends).map(([level, data]: [string, any]) => {
              const sym = trends.currencySymbol ?? '$';
              return (
                <div key={level} className="ad-salary-card">
                  <h4>{level.charAt(0).toUpperCase() + level.slice(1)} Level</h4>
                  <div className="ad-salary-range">
                    <span className="ad-salary-min">{sym}{data.min.toLocaleString()}</span>
                    <span className="ad-salary-median">
                      <strong>{sym}{data.median.toLocaleString()}</strong>
                      <small>median</small>
                    </span>
                    <span className="ad-salary-max">{sym}{data.max.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hot Jobs */}
        <div className="ad-section">
          <h3>🔥 Hot Job Titles</h3>
          <div className="ad-hot-jobs">
            {trends.hotJobs.map((job, idx) => (
              <div key={idx} className="ad-hot-job-item">
                <span className="ad-hot-job-title">{job.title}</span>
                <div className="ad-hot-job-stats">
                  <span className="ad-hot-job-openings">{job.openings.toLocaleString()} openings</span>
                  <span className="ad-hot-job-growth">{job.growth} growth</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Industry Growth */}
        <div className="ad-section">
          <h3>📈 Industry Growth</h3>
          <div className="ad-industry-bars">
            {trends.industryGrowth.map((industry, idx) => (
              <div key={idx} className="ad-industry-item">
                <span className="ad-industry-name">{industry.industry}</span>
                <div className="ad-industry-bar">
                  <div 
                    className="ad-industry-fill"
                    style={{ width: `${industry.growth * 4}%` }}
                  />
                  <span className="ad-industry-value">{industry.growth}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        {trends.recommendations.length > 0 && (
          <div className="ad-section">
            <h3>💡 Recommendations for You</h3>
            <div className="ad-recommendations">
              {trends.recommendations.map((rec, idx) => (
                <div key={idx} className="ad-recommendation-item">
                  <span className="ad-rec-icon">→</span>
                  <span>{rec}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBenchmarksTab = () => {
    if (!overview) return null;
    const b = overview.benchmarks;
    const avgResponseDays = parseInt(overview.averageTimeToResponse) || 0;

    const metrics: { label: string; tooltip: string; yours: number | string; platform: number | string; unit: string; higherIsBetter: boolean }[] = [
      {
        label: "Response Rate",
        tooltip: "The % of your applications that received any reply (interview invite, rejection, or follow-up) from the employer.",
        yours: overview.responseRate,
        platform: b.platformResponseRate,
        unit: "%",
        higherIsBetter: true,
      },
      {
        label: "Interview → Offer",
        tooltip: "Of the interviews you attended, the % that progressed to a job offer.",
        yours: overview.interviewToOfferRate,
        platform: b.platformInterviewToOfferRate,
        unit: "%",
        higherIsBetter: true,
      },
      {
        label: "Avg Time to Response",
        tooltip: "Average number of days between submitting an application and receiving any reply from the employer. Lower is better — it means employers respond to you quickly.",
        yours: avgResponseDays,
        platform: b.platformAvgTimeToResponseDays,
        unit: " days",
        higherIsBetter: false,
      },
    ];

    return (
      <div className="ad-tab-content">
        <div className="ad-section">
          <h3>🏆 How You Compare to the Platform</h3>
          {b.sampleSize > 0 && (
            <p className="ad-bench-sample">Based on data from {b.sampleSize.toLocaleString()} other users</p>
          )}
          <div className="ad-bench-grid">
            {metrics.map((m) => {
              const yoursNum = typeof m.yours === "number" ? m.yours : 0;
              const platformNum = typeof m.platform === "number" ? m.platform : 0;
              const better = m.higherIsBetter ? yoursNum >= platformNum : yoursNum <= platformNum;
              const equal = yoursNum === platformNum;
              const diff = Math.abs(yoursNum - platformNum);
              return (
                <div key={m.label} className={`ad-bench-card ${equal ? "" : better ? "ad-bench-better" : "ad-bench-worse"}`}>
                  <div className="ad-bench-label">
                    {m.label}
                    <span className="ad-bench-tooltip-icon" title={m.tooltip}>ⓘ</span>
                  </div>
                  <div className="ad-bench-row">
                    <div className="ad-bench-col">
                      <span className="ad-bench-value">{m.yours}{m.unit}</span>
                      <span className="ad-bench-sub">You</span>
                    </div>
                    <div className="ad-bench-divider" />
                    <div className="ad-bench-col">
                      <span className="ad-bench-value ad-bench-platform">{m.platform}{m.unit}</span>
                      <span className="ad-bench-sub">Platform avg</span>
                    </div>
                  </div>
                  <div className={`ad-bench-badge ${equal ? "neutral" : better ? "good" : "low"}`}>
                    {equal
                      ? "On par"
                      : better
                      ? `${diff}${m.unit} above average`
                      : `${diff}${m.unit} below average`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ad-section">
          <h3>📋 Weekly Activity</h3>
          <div className="ad-bench-activity-row">
            <div className="ad-bench-activity-card">
              <span className="ad-bench-activity-value">
                {overview.weeklyApplications.length > 0
                  ? (
                      overview.weeklyApplications.reduce((s, w) => s + w.count, 0) /
                      overview.weeklyApplications.filter(w => w.count > 0).length || 1
                    ).toFixed(1)
                  : 0}
              </span>
              <span className="ad-bench-activity-label">Your avg apps/week</span>
            </div>
            <div className="ad-bench-activity-card ad-bench-platform-card">
              <span className="ad-bench-activity-value">{b.platformAvgAppsPerWeek}</span>
              <span className="ad-bench-activity-label">Platform avg apps/week</span>
            </div>
          </div>
        </div>

        <div className="ad-section">
          <h3>💡 Benchmark Insights</h3>
          <div className="ad-insights">
            {overview.responseRate < b.platformResponseRate && (
              <div className="ad-insight-item">
                <span className="ad-insight-icon">💡</span>
                <span>Your response rate is below average — try personalising cover letters or targeting smaller companies.</span>
              </div>
            )}
            {overview.responseRate >= b.platformResponseRate && (
              <div className="ad-insight-item">
                <span className="ad-insight-icon">🎯</span>
                <span>Great response rate! You're beating the platform average — keep up the quality applications.</span>
              </div>
            )}
            {overview.interviewToOfferRate < b.platformInterviewToOfferRate && (
              <div className="ad-insight-item">
                <span className="ad-insight-icon">💡</span>
                <span>Interview-to-offer conversion is below platform average — consider interview coaching or practising answers with AI.</span>
              </div>
            )}
            {avgResponseDays > 0 && avgResponseDays > b.platformAvgTimeToResponseDays + 5 && (
              <div className="ad-insight-item">
                <span className="ad-insight-icon">⏱️</span>
                <span>Employers are taking longer than average to respond to you — consider following up after 1 week.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading || consentsLoading) {
    return (
      <div className="ad-container">
        <div className="ad-loading">
          <div className="ad-spinner" />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  // UC-05/2.1: Analytics & History require data-storage consent. If revoked,
  // grey out the feature and surface a re-enable CTA pointing at Privacy settings.
  if (!consents.data_storage) {
    return (
      <div className="ad-container">
        <div className="ad-header">
          <button onClick={() => navigate("/dashboard")} className="ad-back-btn">
            ← Back to Dashboard
          </button>
          <h1>📊 Analytics Dashboard</h1>
        </div>
        <div style={{
          margin: "24px auto",
          maxWidth: 520,
          padding: 32,
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          textAlign: "center",
          opacity: 0.85,
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <h3 style={{ marginTop: 0 }}>Analytics are disabled</h3>
          <p style={{ color: "#555", lineHeight: 1.5 }}>
            Analytics and history require your consent to store application data.
            Re-enable data storage in Privacy &amp; Permissions to see your insights.
          </p>
          <button
            onClick={() => navigate("/settings?tab=privacy")}
            style={{
              marginTop: 16,
              padding: "10px 24px",
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Manage Privacy Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ad-container">
      <div className="ad-header">
        <button onClick={() => navigate("/dashboard")} className="ad-back-btn">
          ← Back to Dashboard
        </button>
        <h1>📊 Analytics Dashboard</h1>
        <p>Track your job search progress and market insights</p>
      </div>

      {/* Tabs */}
      <div className="ad-tabs">
        <button 
          className={`ad-tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          📈 Overview
        </button>
        <button 
          className={`ad-tab ${activeTab === "funnel" ? "active" : ""}`}
          onClick={() => setActiveTab("funnel")}
        >
          🔄 Funnel
        </button>
        <button
          className={`ad-tab ${activeTab === "trends" ? "active" : ""}`}
          onClick={() => setActiveTab("trends")}
        >
          🔥 Market Trends
        </button>
        <button
          className={`ad-tab ${activeTab === "benchmarks" ? "active" : ""}`}
          onClick={() => setActiveTab("benchmarks")}
        >
          🏆 Benchmarks
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && renderOverviewTab()}
      {activeTab === "funnel" && renderFunnelTab()}
      {activeTab === "trends" && renderTrendsTab()}
      {activeTab === "benchmarks" && renderBenchmarksTab()}
    </div>
  );
};

export default AnalyticsDashboard;
